import { useState, useEffect, useRef } from 'react';
import {
  MessageSquare,
  User,
  Bot,
  Settings2,
  RefreshCw,
  Server,
  Smartphone,
  Clock,
  Hash,
  ChevronRight,
  Globe,
  Cpu,
  Coins,
  Zap,
  ChevronDown,
  ChevronUp,
  Download,
  Trash2,
} from 'lucide-react';
import {
  logs,
  keys,
  settings,
  ApiKey,
  ConversationSummary,
  ConversationMessages,
  DeduplicatedMessage,
  MessagePart,
} from '../api/client';
import { formatDateTime, formatRelativeTime, formatCurrency, cn } from '../lib/utils';

const PAGE_SIZE = 100;

export default function Conversations() {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [conversationMessages, setConversationMessages] = useState<ConversationMessages | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);

  // Filters
  const [appKeyId, setAppKeyId] = useState('');
  const [deviceId, setDeviceId] = useState('');

  // Conversation logging controls
  const [loggingEnabled, setLoggingEnabled] = useState(false);
  const [loggingLoading, setLoggingLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState<number | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const conversationsRequestId = useRef(0);
  const selectRequestId = useRef(0);

  const fetchConversations = async () => {
    const reqId = ++conversationsRequestId.current;
    setLoading(true);
    setLoadingMore(false);
    try {
      const [convsRes, keysRes] = await Promise.all([
        logs.conversations({
          appKeyId: appKeyId || undefined,
          deviceId: deviceId || undefined,
          limit: PAGE_SIZE,
          offset: 0,
        }),
        keys.list(),
      ]);

      if (conversationsRequestId.current !== reqId) return;
      setConversations(convsRes.conversations);
      setTotal(convsRes.total);
      setApiKeys(keysRes.keys);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      if (conversationsRequestId.current === reqId) {
        setLoading(false);
      }
    }
  };

  const loadMoreConversations = async () => {
    if (loadingMore || loading || conversations.length >= total) return;

    const reqId = ++conversationsRequestId.current;
    setLoadingMore(true);
    try {
      const response = await logs.conversations({
        appKeyId: appKeyId || undefined,
        deviceId: deviceId || undefined,
        limit: PAGE_SIZE,
        offset: conversations.length,
      });

      if (conversationsRequestId.current !== reqId) return;
      setConversations(prev => [...prev, ...response.conversations]);
      setTotal(response.total);
    } catch (error) {
      console.error('Failed to load more conversations:', error);
    } finally {
      if (conversationsRequestId.current === reqId) {
        setLoadingMore(false);
      }
    }
  };

  // Load logging toggle state on mount
  useEffect(() => {
    settings.get().then((res) => {
      setLoggingEnabled(res.settings.conversation_logging_enabled === 'true');
    }).catch(() => {}).finally(() => setLoggingLoading(false));
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [appKeyId, deviceId]);

  const handleToggleLogging = async () => {
    const newValue = !loggingEnabled;
    setLoggingEnabled(newValue);
    try {
      await settings.update({ conversation_logging_enabled: newValue ? 'true' : 'false' });
    } catch {
      setLoggingEnabled(!newValue); // Revert on failure
    }
  };

  const handleExportDataset = async () => {
    setExporting(true);
    try {
      const blob = await logs.exportDataset({
        appKeyId: appKeyId || undefined,
        deviceId: deviceId || undefined,
        minTurns: 3,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dataset-${new Date().toISOString().slice(0, 10)}.jsonl`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Dataset export failed:', error);
    } finally {
      setExporting(false);
    }
  };

  const handlePurge = async () => {
    setPurging(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const result = await logs.purgeContent({
        startDate: '2020-01-01',
        endDate: today,
        appKeyId: appKeyId || undefined,
        deviceId: deviceId || undefined,
      });
      setPurgeResult(result.rowsPurged);
      setPurgeConfirm(false);
      fetchConversations();
      setTimeout(() => setPurgeResult(null), 3000);
    } catch (error) {
      console.error('Purge failed:', error);
    } finally {
      setPurging(false);
    }
  };

  const handleSelectConversation = async (conversationId: string) => {
    const reqId = ++selectRequestId.current;
    setSelectedConversation(conversationId);
    setConversationMessages(null);
    setMessagesLoading(true);

    try {
      const response = await logs.conversationMessages(conversationId);
      if (selectRequestId.current !== reqId) return;
      setConversationMessages(response);
    } catch (error) {
      if (selectRequestId.current !== reqId) return;
      console.error('Failed to fetch conversation messages:', error);
    } finally {
      if (selectRequestId.current === reqId) {
        setMessagesLoading(false);
      }
    }
  };

  const getAppName = (keyId: string) => {
    return apiKeys.find((k) => k.id === keyId)?.appName || keyId.slice(0, 8);
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex gap-6 animate-fade-in-up">
      {/* Conversations List */}
      <div className="w-80 card-glow rounded-2xl flex flex-col overflow-hidden">
        <div className="p-5 border-b border-border">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="w-5 h-5 text-cyan-400" />
            <h1 className="text-lg font-semibold">Conversations</h1>
          </div>

          {/* Filters */}
          <div className="space-y-3">
            <div className="relative">
              <Server className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <select
                value={appKeyId}
                onChange={(e) => {
                  conversationsRequestId.current += 1;
                  setAppKeyId(e.target.value);
                }}
                className="input-neural w-full pl-10 pr-4 py-2.5 rounded-xl text-sm appearance-none cursor-pointer"
              >
                <option value="">All Apps</option>
                {apiKeys.map((key) => (
                  <option key={key.id} value={key.id}>
                    {key.appName}
                  </option>
                ))}
              </select>
            </div>
            <div className="relative">
              <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={deviceId}
                onChange={(e) => {
                  conversationsRequestId.current += 1;
                  setDeviceId(e.target.value);
                }}
                placeholder="Filter by device ID"
                className="input-neural w-full pl-10 pr-4 py-2.5 rounded-xl text-sm"
              />
            </div>
          </div>

          {/* Logging Controls */}
          <div className="mt-4 pt-4 border-t border-border space-y-3">
            {/* Toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">Logging</span>
                {loggingEnabled && (
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-live" />
                    <span className="text-[10px] text-emerald-400 font-medium">Recording</span>
                  </span>
                )}
              </div>
              <button
                onClick={handleToggleLogging}
                disabled={loggingLoading}
                className={cn(
                  'w-9 h-5 rounded-full transition-colors relative',
                  loggingEnabled ? 'bg-emerald-500' : 'bg-background border border-border'
                )}
              >
                <div className={cn(
                  'w-3.5 h-3.5 rounded-full bg-white absolute top-[3px] transition-all',
                  loggingEnabled ? 'left-[18px]' : 'left-[3px]'
                )} />
              </button>
            </div>

            {/* Export + Purge */}
            <div className="flex gap-2">
              <button
                onClick={handleExportDataset}
                disabled={exporting}
                className="btn-secondary flex-1 px-3 py-2 rounded-xl flex items-center justify-center gap-1.5 text-xs"
              >
                {exporting ? (
                  <RefreshCw className="w-3 h-3 animate-spin" />
                ) : (
                  <Download className="w-3 h-3" />
                )}
                {exporting ? 'Exporting...' : 'Export'}
              </button>

              {purgeResult !== null ? (
                <div className="flex-1 px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs text-center">
                  {purgeResult} rows purged
                </div>
              ) : purgeConfirm ? (
                <div className="flex-1 flex gap-1">
                  <button
                    onClick={handlePurge}
                    disabled={purging}
                    className="flex-1 px-2 py-2 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-400 text-xs"
                  >
                    {purging ? 'Purging...' : 'Confirm'}
                  </button>
                  <button
                    onClick={() => setPurgeConfirm(false)}
                    className="px-2 py-2 rounded-xl bg-background border border-border text-muted-foreground text-xs"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setPurgeConfirm(true)}
                  className="flex-1 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center gap-1.5 text-xs hover:bg-amber-500/15 transition-colors"
                >
                  <Trash2 className="w-3 h-3" />
                  Purge
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Loading...
              </div>
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
              <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center mb-3">
                <MessageSquare className="w-6 h-6" />
              </div>
              <p className="text-sm">No conversations found</p>
            </div>
          ) : (
            <div className="py-2">
              {conversations.map((conv, index) => (
                <button
                  key={conv.conversationId}
                  onClick={() => handleSelectConversation(conv.conversationId)}
                  className={cn(
                    'w-full px-4 py-3 text-left transition-all animate-fade-in-up',
                    selectedConversation === conv.conversationId
                      ? 'bg-cyan-500/10 border-l-2 border-cyan-500'
                      : 'hover:bg-secondary/50 border-l-2 border-transparent'
                  )}
                  style={{ animationDelay: `${Math.min(index, 15) * 30}ms` }}
                >
                  {/* Preview text. Coerced to string defensively so a stale
                      worker version returning a vision content array can't
                      blank the list. */}
                  <p
                    className={cn(
                      'text-sm font-medium truncate mb-1',
                      selectedConversation === conv.conversationId ? 'text-cyan-400' : ''
                    )}
                  >
                    {previewText(conv.firstMessagePreview) || 'No preview available'}
                  </p>

                  {/* Model + time */}
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground font-mono bg-secondary/50 px-1.5 py-0.5 rounded">
                      {conv.model}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatRelativeTime(conv.lastActivity)}
                    </span>
                  </div>

                  {/* Device + message count */}
                  <div className="flex items-center justify-between text-xs text-muted-foreground/70">
                    <div className="flex items-center gap-1">
                      <DeviceIcon deviceId={conv.deviceId} />
                      <span className="truncate max-w-[120px]">
                        {getAppName(conv.appKeyId)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Hash className="w-3 h-3" />
                      <span>{conv.requestCount} turns</span>
                    </div>
                  </div>

                  {/* Inferred indicator */}
                  {conv.isInferred && (
                    <div className="mt-1">
                      <span className="text-[10px] text-amber-400/70 border border-amber-500/20 px-1.5 py-0.5 rounded">
                        inferred
                      </span>
                    </div>
                  )}
                </button>
              ))}
              {conversations.length < total && (
                <div className="px-4 pt-2 pb-3">
                  <button
                    onClick={loadMoreConversations}
                    disabled={loadingMore}
                    className="btn-secondary w-full px-3 py-2 rounded-xl flex items-center justify-center gap-1.5 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingMore && <RefreshCw className="w-3 h-3 animate-spin" />}
                    {loadingMore ? 'Loading...' : 'Load more'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Chat View */}
      <div className="flex-1 card-glow rounded-2xl flex flex-col overflow-hidden">
        {!selectedConversation ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
            <div className="w-20 h-20 rounded-2xl bg-secondary flex items-center justify-center mb-4">
              <MessageSquare className="w-10 h-10" />
            </div>
            <p className="text-lg font-medium mb-1">Select a conversation</p>
            <p className="text-sm text-muted-foreground/70">
              Choose from the list to view messages
            </p>
          </div>
        ) : messagesLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center gap-3 text-muted-foreground">
              <RefreshCw className="w-5 h-5 animate-spin" />
              Loading messages...
            </div>
          </div>
        ) : conversationMessages ? (
          <>
            {/* Header */}
            <div className="px-6 py-4 border-b border-border bg-secondary/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold">
                      {getAppName(conversationMessages.appKeyId)}
                    </h2>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <DeviceIcon deviceId={conversationMessages.deviceId} />
                        <DeviceLabel deviceId={conversationMessages.deviceId} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1.5" title="Model">
                    <Cpu className="w-3.5 h-3.5" />
                    <span className="font-mono text-xs">{conversationMessages.model}</span>
                  </div>
                  <div className="flex items-center gap-1.5" title="Turns">
                    <Hash className="w-3.5 h-3.5" />
                    <span>{conversationMessages.requestCount}</span>
                  </div>
                  <div className="flex items-center gap-1.5" title="Tokens">
                    <Zap className="w-3.5 h-3.5" />
                    <span>
                      {(
                        conversationMessages.totalInputTokens +
                        conversationMessages.totalOutputTokens
                      ).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5" title="Cost">
                    <Coins className="w-3.5 h-3.5" />
                    <span>{formatCurrency(conversationMessages.totalCost)}</span>
                  </div>
                  <div className="flex items-center gap-1.5" title="Time range">
                    <Clock className="w-3.5 h-3.5" />
                    <span className="text-xs">
                      {formatDateTime(conversationMessages.startedAt)}
                    </span>
                    <ChevronRight className="w-3 h-3" />
                    <span className="text-xs">
                      {formatDateTime(conversationMessages.lastActivity)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {conversationMessages.messages.map((msg, index) => (
                <ChatBubble key={`${msg.requestId}-${index}`} message={msg} index={index} />
              ))}
              <div ref={messagesEndRef} />
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

// Coerce a preview value to a safe string for rendering. The wire type is
// `string`, but a stale worker without normalizeContent could still return
// a vision content array. Without this, React would throw and blank the
// page. Extracts text parts when available, returns "[image]" otherwise.
function previewText(preview: unknown): string {
  if (typeof preview === 'string') return preview;
  if (Array.isArray(preview)) {
    const pieces: string[] = [];
    for (const p of preview as Array<Record<string, unknown>>) {
      if (p && typeof p === 'object') {
        if (p.type === 'text' && typeof p.text === 'string') pieces.push(p.text);
        else if (p.type === 'image_url') pieces.push('[image]');
      }
    }
    return pieces.join(' ').trim();
  }
  return '';
}

// Small device icon component
function DeviceIcon({ deviceId }: { deviceId: string | null }) {
  if (!deviceId || deviceId.startsWith('unknown-')) {
    return <Globe className="w-3 h-3" />;
  }
  if (deviceId.startsWith('ip-')) {
    return <Globe className="w-3 h-3" />;
  }
  return <Smartphone className="w-3 h-3" />;
}

// Device label component
function DeviceLabel({ deviceId }: { deviceId: string | null }) {
  if (!deviceId || deviceId.startsWith('unknown-')) {
    return <span>Unknown Client</span>;
  }
  if (deviceId.startsWith('ip-')) {
    return <span className="font-mono">IP: {deviceId.slice(3)}</span>;
  }
  return <span className="font-mono">{deviceId.slice(0, 16)}...</span>;
}

function ChatBubble({ message, index }: { message: DeduplicatedMessage; index: number }) {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';

  if (isSystem) {
    return <SystemMessage message={message} index={index} />;
  }

  return (
    <div
      className={cn('flex animate-fade-in-up', isUser ? 'justify-end' : 'justify-start')}
      style={{ animationDelay: `${Math.min(index, 20) * 50}ms` }}
    >
      <div
        className={cn(
          'max-w-2xl px-5 py-3 rounded-xl',
          isUser
            ? 'bg-gradient-to-br from-cyan-500 to-cyan-600 text-[hsl(222,47%,6%)]'
            : 'bg-secondary border border-border'
        )}
      >
        <div className="flex items-center gap-2 mb-2">
          {isUser ? (
            <>
              <User className="w-4 h-4" />
              <span className="text-xs font-medium uppercase opacity-80">You</span>
            </>
          ) : (
            <>
              <Bot className="w-4 h-4 text-violet-400" />
              <span className="text-xs font-medium text-violet-400 uppercase">Assistant</span>
              <span className="text-[10px] font-mono text-muted-foreground bg-secondary/50 px-1.5 py-0.5 rounded ml-1">
                {message.model}
              </span>
            </>
          )}
        </div>
        <MessageBody
          message={message}
          textClassName={cn('whitespace-pre-wrap text-sm', isUser ? '' : 'text-foreground/90')}
        />
      </div>
    </div>
  );
}

// Renders a message body. When `parts` is present (vision content array)
// each text part becomes a paragraph and each image_url part renders as an
// inline thumbnail. Otherwise falls back to the plain `content` string.
//
// `String(...)` guard is defensive: even if a future API regression sends a
// non-string here, React will get a string instead of crashing the page.
function MessageBody({
  message,
  textClassName,
}: {
  message: DeduplicatedMessage;
  textClassName: string;
}) {
  if (message.parts && message.parts.length > 0) {
    return (
      <div className="space-y-2">
        {message.parts.map((part, i) => (
          <MessagePartView key={i} part={part} textClassName={textClassName} />
        ))}
      </div>
    );
  }

  return <p className={textClassName}>{String(message.content ?? '')}</p>;
}

function MessagePartView({
  part,
  textClassName,
}: {
  part: MessagePart;
  textClassName: string;
}) {
  if (part.type === 'image_url' && part.imageUrl) {
    return (
      <a
        href={part.imageUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block rounded-lg overflow-hidden border border-border/40 bg-background/40 hover:border-cyan-500/40 transition-colors"
        title="Open image in new tab"
      >
        <img
          src={part.imageUrl}
          alt="Attached"
          className="max-w-full max-h-80 object-contain"
          loading="lazy"
        />
      </a>
    );
  }
  if (part.type === 'text' && part.text) {
    return <p className={textClassName}>{part.text}</p>;
  }
  return null;
}

// System message with collapsible content (system prompts are usually very long)
function SystemMessage({ message, index }: { message: DeduplicatedMessage; index: number }) {
  const [expanded, setExpanded] = useState(false);
  // Defensive String() — content is typed as string but historical/stale
  // payloads might contain non-strings; we don't want a system prompt to
  // blank the entire conversation view.
  const content = String(message.content ?? '');
  const isLong = content.length > 200;

  return (
    <div
      className="flex justify-center animate-fade-in-up"
      style={{ animationDelay: `${Math.min(index, 20) * 50}ms` }}
    >
      <div className="bg-amber-500/10 border border-amber-500/20 text-amber-200 px-5 py-3 rounded-xl text-sm max-w-2xl w-full">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 mb-2 w-full text-left"
        >
          <Settings2 className="w-4 h-4 text-amber-400" />
          <span className="text-xs font-medium text-amber-400 uppercase">System Prompt</span>
          {isLong && (
            <span className="ml-auto">
              {expanded ? (
                <ChevronUp className="w-3.5 h-3.5 text-amber-400/60" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5 text-amber-400/60" />
              )}
            </span>
          )}
        </button>
        <p
          className={cn(
            'whitespace-pre-wrap text-foreground/80 transition-all',
            !expanded && isLong ? 'line-clamp-3' : ''
          )}
        >
          {content}
        </p>
        {!expanded && isLong && (
          <button
            onClick={() => setExpanded(true)}
            className="text-xs text-amber-400/60 mt-1 hover:text-amber-400"
          >
            Show full prompt...
          </button>
        )}
      </div>
    </div>
  );
}
